"use client";

/**
 * Error boundary for FormModal contents.
 *
 * Without this, a runtime error inside the form renderer (e.g. a malformed field
 * config) unmounts the entire React tree and leaves the user on a white screen.
 * With it, the modal stays open and offers a Close button so the user can recover.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface FormModalErrorBoundaryProps {
  /** Called when the user clicks Close from the error state */
  onClose: () => void;
  children: ReactNode;
}

interface FormModalErrorBoundaryState {
  hasError: boolean;
}

export class FormModalErrorBoundary extends Component<FormModalErrorBoundaryProps, FormModalErrorBoundaryState> {
  state: FormModalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): FormModalErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("FormModalErrorBoundary: caught render error", error, info);
  }

  handleClose = (): void => {
    this.setState({ hasError: false });
    this.props.onClose();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
        <h3 className="text-xl font-bold text-gray-900">Something went wrong</h3>
        <p className="max-w-sm text-sm font-medium text-gray-600">
          We hit a problem displaying this form. Please close this window and try again.
        </p>
        <button
          type="button"
          onClick={this.handleClose}
          className="touch-manipulation rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    );
  }
}
